import React, { useEffect, useMemo, useState } from 'react';

const HOUR_PX = 52;
const DAY_START = 8 * 60;
const DAY_END = 20 * 60;

const STATUS_COLOR = {
  Scheduled: '#0d6efd',
  Completed: '#6c757d',
  Cancelled: '#dc3545',
  Rescheduled: '#fd7e14'
};

function pad(value) {
  return String(value).padStart(2, '0');
}

function dateKey(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function keyFromValue(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return dateKey(date);
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return startOfDay(next);
}

function startOfWeek(date) {
  const day = startOfDay(date);
  const offset = (day.getDay() + 6) % 7;
  return addDays(day, -offset);
}

function sameDay(left, right) {
  return dateKey(left) === dateKey(right);
}

function formatTitle(view, anchor) {
  const full = anchor.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  if (view === 'daily') return full;
  const start = startOfWeek(anchor);
  const end = addDays(start, 6);
  const sameMonth = start.getMonth() === end.getMonth();
  const startText = start.toLocaleDateString(undefined, { day: 'numeric', month: sameMonth ? undefined : 'short' });
  const endText = end.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  return `${startText} – ${endText}`;
}

function parseClock(text) {
  const match = String(text || '').trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i);
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = Number(match[2] || 0);
  const meridiem = (match[3] || '').toLowerCase();
  if (hour > 23 || minute > 59) return null;
  if (meridiem === 'pm' && hour < 12) hour += 12;
  if (meridiem === 'am' && hour === 12) hour = 0;
  if (!meridiem && hour >= 1 && hour <= 6) hour += 12;
  return hour * 60 + minute;
}

function parseSlot(slot) {
  const text = String(slot || '').trim();
  const parts = text.split(/\s*[-–]\s*/);
  if (parts.length >= 2) {
    const start = parseClock(parts[0]);
    const end = parseClock(parts[1]);
    if (start != null && end != null && end > start) return { start, end };
    if (start != null) return { start, end: Math.min(start + 60, 24 * 60) };
  }
  const start = parseClock(text);
  if (start == null) return null;
  return { start, end: Math.min(start + 30, 24 * 60) };
}

function layoutEvents(events) {
  const items = events.map((event) => ({ ...event })).sort((a, b) => a.start - b.start || a.end - b.end);
  const clusters = [];
  items.forEach((event) => {
    let cluster = clusters.find((group) => group.end > event.start);
    if (!cluster) {
      cluster = { end: event.end, items: [] };
      clusters.push(cluster);
    }
    cluster.end = Math.max(cluster.end, event.end);
    const used = new Set(cluster.items.filter((item) => item.end > event.start).map((item) => item.column));
    let column = 0;
    while (used.has(column)) column += 1;
    event.column = column;
    cluster.items.push(event);
  });
  clusters.forEach((cluster) => {
    const columns = Math.max(...cluster.items.map((item) => item.column)) + 1;
    cluster.items.forEach((item) => {
      item.columns = columns;
    });
  });
  return items;
}

function AppointmentCalendar({ appointments, onSelect, onSlotSelect, focusDate, draft, maxHeight = 680 }) {
  const [view, setView] = useState('weekly');
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const today = startOfDay(new Date());
  useEffect(() => {
    if (!focusDate) return;
    const [year, month, day] = String(focusDate).split('-').map(Number);
    if (!year || !month || !day) return;
    setAnchor(new Date(year, month - 1, day));
  }, [focusDate]);
  const days = useMemo(() => {
    const start = view === 'daily' ? startOfDay(anchor) : startOfWeek(anchor);
    const count = view === 'daily' ? 1 : 7;
    return Array.from({ length: count }, (_, index) => addDays(start, index));
  }, [view, anchor]);

  const placed = useMemo(() => {
    const byDay = {};
    const untimed = {};
    days.forEach((day) => {
      byDay[dateKey(day)] = [];
      untimed[dateKey(day)] = [];
    });
    appointments.forEach((appointment) => {
      const key = keyFromValue(appointment.APPOINTMENT_DATE);
      if (!byDay[key]) return;
      const slot = parseSlot(appointment.APPOINTMENT_SLOT);
      const item = {
        appointment,
        label: appointment.PATIENT_ID?.PATIENT_NAME || 'Patient',
        place: appointment.APPOINTMENT_PLACE || '',
        slot: appointment.APPOINTMENT_SLOT || '',
        status: appointment.APPOINTMENT_STATUS || 'Scheduled'
      };
      if (!slot) untimed[key].push(item);
      else byDay[key].push({ ...item, start: slot.start, end: slot.end });
    });
    Object.keys(byDay).forEach((key) => {
      byDay[key] = layoutEvents(byDay[key]);
    });
    return { byDay, untimed };
  }, [appointments, days]);

  const timed = Object.values(placed.byDay).flat();
  const first = timed.length ? Math.min(...timed.map((item) => item.start)) : DAY_START;
  const last = timed.length ? Math.max(...timed.map((item) => item.end)) : DAY_END;
  const startMinute = Math.min(DAY_START, Math.floor(first / 60) * 60);
  const endMinute = Math.max(DAY_END, Math.ceil(last / 60) * 60);
  const hours = [];
  for (let minute = startMinute; minute < endMinute; minute += 60) hours.push(minute);
  const gridHeight = hours.length * HOUR_PX;

  const move = (direction) => {
    setAnchor(addDays(anchor, direction * (view === 'daily' ? 1 : 7)));
  };

  const now = new Date();
  const nowMinute = now.getHours() * 60 + now.getMinutes();
  const showNow = nowMinute >= startMinute && nowMinute <= endMinute && days.some((day) => sameDay(day, today));

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem' }}>
        <button type="button" onClick={() => setAnchor(today)} style={toolbarButton}>Today</button>
        <button type="button" aria-label="Previous" onClick={() => move(-1)} style={toolbarButton}>‹</button>
        <button type="button" aria-label="Next" onClick={() => move(1)} style={toolbarButton}>›</button>
        <strong style={{ marginRight: 'auto' }}>{formatTitle(view, anchor)}</strong>
        {['daily', 'weekly'].map((option) => (
          <button key={option} type="button" onClick={() => setView(option)} style={{ ...toolbarButton, backgroundColor: view === option ? '#007bff' : 'white', color: view === option ? 'white' : '#007bff' }}>
            {option === 'daily' ? 'Daily' : 'Weekly'}
          </button>
        ))}
      </div>
      <div style={{ border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'auto', maxHeight, backgroundColor: 'white' }}>
        <div style={{ display: 'grid', gridTemplateColumns: `72px repeat(${days.length}, minmax(120px, 1fr))`, minWidth: view === 'daily' ? 360 : 860, position: 'sticky', top: 0, zIndex: 2, backgroundColor: 'white', borderBottom: '1px solid #dee2e6' }}>
          <div />
          {days.map((day) => {
            const current = sameDay(day, today);
            return (
              <div key={dateKey(day)} style={{ padding: '8px', textAlign: 'center', borderLeft: '1px solid #f1f3f5' }}>
                <div style={{ fontSize: '0.75rem', color: '#6c757d', textTransform: 'uppercase' }}>{day.toLocaleDateString(undefined, { weekday: 'short' })}</div>
                <div style={{ width: 32, height: 32, lineHeight: '32px', margin: '2px auto 0', borderRadius: '50%', backgroundColor: current ? '#007bff' : 'transparent', color: current ? 'white' : '#212529', fontWeight: 700 }}>
                  {day.getDate()}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: `72px repeat(${days.length}, minmax(120px, 1fr))`, minWidth: view === 'daily' ? 360 : 860 }}>
          <div />
          {days.map((day) => (
            <div key={`${dateKey(day)}-untimed`} style={{ minHeight: 28, borderLeft: '1px solid #f1f3f5', padding: 4 }}>
              {placed.untimed[dateKey(day)].map((item) => (
                <EventButton key={item.appointment.APPOINTMENT_ID || item.appointment._id} item={item} onSelect={onSelect} />
              ))}
            </div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: `72px repeat(${days.length}, minmax(120px, 1fr))`, minWidth: view === 'daily' ? 360 : 860, position: 'relative' }}>
          <div>
            {hours.map((minute) => (
              <div key={minute} style={{ height: HOUR_PX, textAlign: 'right', paddingRight: 8, color: '#6c757d', fontSize: '0.75rem' }}>
                {clockLabel(minute)}
              </div>
            ))}
          </div>
          {days.map((day) => {
            const key = dateKey(day);
            return (
              <div
                key={key}
                onClick={onSlotSelect ? (event) => pickSlot(event, day, startMinute, endMinute, onSlotSelect) : undefined}
                style={{ position: 'relative', height: gridHeight, borderLeft: '1px solid #f1f3f5', cursor: onSlotSelect ? 'pointer' : 'default', backgroundImage: `repeating-linear-gradient(to bottom, transparent, transparent ${HOUR_PX - 1}px, #e9ecef ${HOUR_PX - 1}px, #e9ecef ${HOUR_PX}px)` }}
              >
                {showNow && sameDay(day, today) && (
                  <div style={{ position: 'absolute', top: ((nowMinute - startMinute) / 60) * HOUR_PX, left: 0, right: 0, borderTop: '2px solid #dc3545', zIndex: 1, pointerEvents: 'none' }} />
                )}
                {draft && draft.date === key && draft.minutes != null && (
                  <div style={{ position: 'absolute', top: ((draft.minutes - startMinute) / 60) * HOUR_PX, height: Math.max((30 / 60) * HOUR_PX, 24), left: 2, right: 2, border: '2px dashed #1f4d40', backgroundColor: '#1f4d4022', borderRadius: 4, padding: '2px 6px', fontSize: '0.78rem', pointerEvents: 'none', zIndex: 1 }}>
                    <strong>New visit</strong>
                    <span style={{ display: 'block' }}>{formatRange(draft.minutes, draft.minutes + 30)}</span>
                  </div>
                )}
                {placed.byDay[key].map((item) => {
                  const top = ((item.start - startMinute) / 60) * HOUR_PX;
                  const height = Math.max(((item.end - item.start) / 60) * HOUR_PX, 24);
                  const width = `calc(${100 / item.columns}% - 4px)`;
                  const left = `calc(${(100 / item.columns) * item.column}% + 2px)`;
                  return (
                    <EventButton key={item.appointment.APPOINTMENT_ID || item.appointment._id} item={item} onSelect={onSelect} style={{ position: 'absolute', top, height, width, left, overflow: 'hidden' }} />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function clockLabel(minutes) {
  const hour = Math.floor(minutes / 60);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const shown = hour % 12 || 12;
  return `${shown} ${suffix}`;
}

function pickSlot(event, day, startMinute, endMinute, onSlotSelect) {
  const rect = event.currentTarget.getBoundingClientRect();
  const raw = startMinute + ((event.clientY - rect.top) / HOUR_PX) * 60;
  const snapped = Math.round(raw / 15) * 15;
  const minutes = Math.max(startMinute, Math.min(snapped, endMinute - 15));
  onSlotSelect({ date: dateKey(day), minutes });
}

function formatRange(start, end) {
  return `${clockLabelWithMinutes(start)} - ${clockLabelWithMinutes(end)}`;
}

function clockLabelWithMinutes(minutes) {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const shown = hour % 12 || 12;
  return `${shown}:${pad(minute)} ${suffix}`;
}

export function appointmentDayKey(value) {
  return keyFromValue(value);
}

export function slotRange(slot) {
  return parseSlot(slot);
}

function EventButton({ item, onSelect, style }) {
  const color = STATUS_COLOR[item.status] || '#0f766e';
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        if (onSelect) onSelect(item.appointment);
      }}
      title={`${item.label}, ${item.slot}${item.place ? `, ${item.place}` : ''}`}
      style={{
        display: 'block',
        textAlign: 'left',
        border: 'none',
        borderLeft: `3px solid ${color}`,
        backgroundColor: `${color}22`,
        color: '#212529',
        borderRadius: 4,
        padding: '2px 6px',
        cursor: 'pointer',
        fontSize: '0.78rem',
        lineHeight: 1.25,
        marginBottom: 4,
        ...style
      }}
    >
      <strong style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</strong>
      <span style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.slot}{item.place ? ` · ${item.place}` : ''}</span>
    </button>
  );
}

const toolbarButton = {
  padding: '6px 12px',
  borderRadius: 4,
  border: '1px solid #007bff',
  backgroundColor: 'white',
  color: '#007bff',
  cursor: 'pointer',
  fontWeight: 600
};

export default AppointmentCalendar;
